import TitleIdentifier from "@components/dashboard/TitleIdentifier";
import TitleNote from "@components/dashboard/TitleNote";
import { useEffect, useRef, useState } from "react";
import { formatNumberToLocale } from "@utils/formatNumber";
import { fetchCostOriginTitles } from "./costOriginApi";
import AppropriationEditor from "./AppropriationEditor";
import AnnulledTitlesList from "./AnnulledTitlesList";

// Drill-down do card "Origem do custo": offcanvas que lista os títulos do Sienge de
// um balde (suprimentos | financeiro), agrupados por nº do título (id), com sub-filtro
// por código de origem (CP/GI/...). Reusa as classes .offcanvas do design system.

const PAGE_SIZE = 50;

const BUCKET_LABEL = { supplies: "Via suprimentos", finance: "Via financeiro" };

// Rótulos amigáveis dos códigos de origem do Sienge (fallback = código cru).
// Podem crescer conforme o ETL backfilla mais obras.
const ORIGIN_LABEL = {
  AC: "Pedido de compra",
  CO: "Contrato",
  ME: "Medição",
  CP: "Lançado direto (contas a pagar)",
  GI: "Guia de imposto",
  RF: "Financiamento",
  DV: "Devolução",
};
const originLabel = (code) => ORIGIN_LABEL[code] || code || "—";

// Situação do título a partir do split pago/a pagar.
const stageLabel = (paid = 0, toPay = 0) => {
  if (paid > 0 && toPay > 0) return "Pago + a pagar";
  if (toPay > 0) return "A pagar";
  return "Pago";
};

// Data ISO (YYYY-MM-DD) → DD/MM/YYYY.
const formatDate = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};

const th = { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #e0e0e0", fontWeight: 600, whiteSpace: "nowrap" };
const thR = { ...th, textAlign: "right" };
const td = { padding: "8px 10px", borderBottom: "1px solid #eee", verticalAlign: "top" };
const num = { ...td, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };

export const canEditCostOriginAppropriation = (meta, readOnly) => (
  readOnly !== true && meta?.writebackEnabled === true
);

const CostOriginTitlesOffcanvas = ({ open, onClose, projectId, bucket, header, readOnly = false }) => {
  const [originFilter, setOriginFilter] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ totalValue: 0, count: 0 });
  const [breakdown, setBreakdown] = useState([]);
  const [annulled, setAnnulled] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [editingBillId, setEditingBillId] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const editTriggerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce da busca para não disparar uma requisição por tecla.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Troca de balde limpa o sub-filtro (CP/GI do financeiro não vale pra suprimentos) e a busca.
  useEffect(() => {
    setOriginFilter(null);
    setSearch("");
    setEditingBillId(null);
    setEditingTitle(null);
  }, [bucket]);

  const backToList = () => {
    const triggerBillId = String(editingBillId ?? "");
    setEditingBillId(null);
    setEditingTitle(null);
    setTimeout(() => {
      const currentTrigger = [...document.querySelectorAll("[data-cost-origin-edit-trigger]")]
        .find((element) => element.dataset.costOriginEditTrigger === triggerBillId);
      (currentTrigger ?? editTriggerRef.current)?.focus();
    }, 0);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      if (editingBillId) backToList();
      else onClose?.();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, editingBillId, onClose]);

  // Busca a 1ª página ao abrir / trocar balde / trocar sub-filtro. Também reusada
  // como refresh após salvar uma edição de apropriação no AppropriationEditor.
  const loadFirstPage = async () => {
    setLoading(true);
    setError(false);
    const data = await fetchCostOriginTitles(projectId, {
      bucket,
      origin: originFilter,
      search: debouncedSearch,
      page: 0,
      pageSize: PAGE_SIZE,
    });
    if (!data) {
      setError(true);
      setLoading(false);
      return;
    }
    setMeta(data.meta ?? { totalValue: 0, count: 0 });
    setBreakdown(data.breakdown ?? []);
    setAnnulled(data.annulled ?? []);
    setHasMore(!!data.hasMore);
    setRows(data.titles ?? []);
    setPage(0);
    setLoading(false);
  };

  useEffect(() => {
    if (!open || !projectId || !bucket) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const data = await fetchCostOriginTitles(projectId, {
        bucket,
        origin: originFilter,
        search: debouncedSearch,
        page: 0,
        pageSize: PAGE_SIZE,
      });
      if (cancelled) return;
      if (!data) {
        setError(true);
        setLoading(false);
        return;
      }
      setMeta(data.meta ?? { totalValue: 0, count: 0 });
      setBreakdown(data.breakdown ?? []);
      setAnnulled(data.annulled ?? []);
      setHasMore(!!data.hasMore);
      setRows(data.titles ?? []);
      setPage(0);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, bucket, originFilter, debouncedSearch]);

  const loadMore = async () => {
    if (loading) return;
    const next = page + 1;
    setLoading(true);
    const data = await fetchCostOriginTitles(projectId, {
      bucket,
      origin: originFilter,
      search: debouncedSearch,
      page: next,
      pageSize: PAGE_SIZE,
    });
    if (!data) {
      setError(true);
      setLoading(false);
      return;
    }
    setRows((prev) => [...prev, ...(data.titles ?? [])]);
    setHasMore(!!data.hasMore);
    setPage(next);
    setLoading(false);
  };

  const title = BUCKET_LABEL[bucket] ?? "Títulos";
  const pct = header?.pct;
  const writebackEnabled = canEditCostOriginAppropriation(meta, readOnly);

  return (
    <>
      <div className={open ? "offcanvas active" : "offcanvas"}>
        <div className="offcanvas-container large cost-origin-titles-surface">
          <hgroup className="offcanvas-header">
            <h2>{editingBillId ? `Editar apropriação — título ${editingBillId}` : `Origem do custo — ${title}`}</h2>
            <button onClick={onClose} aria-label="Fechar">
              <i className="icon icon-width-24 icon-times background-accent" aria-hidden={true}></i>
            </button>
          </hgroup>
          <div className="offcanvas-content">
            {!editingBillId ? (
              <>
            <p className="card-label margin-top-8">
              {`${meta.count} ${meta.count === 1 ? "título" : "títulos"} · total R$ ${formatNumberToLocale(meta.totalValue)}`}
              {Number.isFinite(Number(pct)) ? ` (${Number(pct).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}% do custo)` : ""}
            </p>

            {/* Busca por nº do título (Sienge) ou credor */}
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nº do título ou credor…"
              className="margin-top-8 cost-origin-titles-search"
            />

            {/* Sub-filtro por código de origem (CP/GI/...) — reusa o estilo dos toggles do offcanvas */}
            <div className="flex gap-8 margin-top-8 offcanvas-buttons cost-origin-titles-filters">
              <button className={originFilter === null ? "active" : undefined} onClick={() => setOriginFilter(null)}>
                Todos
              </button>
              {breakdown.map((b) => (
                <button
                  key={b.origin || "empty"}
                  className={originFilter === b.origin ? "active" : undefined}
                  onClick={() => setOriginFilter(b.origin)}
                  title={originLabel(b.origin)}
                >
                  {`${b.origin || "—"} · ${b.count}`}
                </button>
              ))}
            </div>

            <div className="cost-origin-titles-table">
              <table>
                <thead>
                  <tr>
                    <th style={th}>Nº Sienge</th>
                    <th style={th}>Credor</th>
                    <th style={th}>Tipo</th>
                    <th style={th}>Origem</th>
                    <th style={th}>Situação</th>
                    <th style={thR}>Valor</th>
                    <th style={th}>Data</th>
                    {writebackEnabled && <th style={th}></th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ ...td, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        <TitleIdentifier row={r} />
                        <TitleNote note={r.note} />
                      </td>
                      <td style={td}>{r.creditor_name || "—"}</td>
                      <td style={td}>{r.document || "—"}</td>
                      <td style={td} title={originLabel(r.origin)}>{r.origin || "—"}</td>
                      <td style={td}>{stageLabel(r.paidValue, r.toPayValue)}</td>
                      <td style={num}>{`R$ ${formatNumberToLocale(r.value)}`}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{formatDate(r.date)}</td>
                      {writebackEnabled && (
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          <button
                            className="button button-accent small"
                            data-cost-origin-edit-trigger={r.id}
                            onClick={(event) => {
                              editTriggerRef.current = event.currentTarget;
                              setEditingBillId(r.id);
                              setEditingTitle({
                                creditor: r.creditor_name,
                                document: r.document,
                                value: r.value,
                                date: r.date,
                                situation: stageLabel(r.paidValue, r.toPayValue),
                              });
                            }}
                          >
                            Editar apropriação
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {loading && <p className="card-label margin-top-16">Carregando…</p>}
            {error && !loading && (
              <p className="empty-state margin-top-16">Não foi possível carregar os títulos. Tente novamente.</p>
            )}
            {!loading && !error && rows.length === 0 && (
              <p className="empty-state margin-top-16">Nenhum título neste balde.</p>
            )}

            {hasMore && !loading && (
              <div className="flex justify-content-center margin-top-16">
                <button className="button button-accent small" onClick={loadMore}>
                  Carregar mais
                </button>
              </div>
            )}
            {/* Fora do total e da lista acima: título anulado na Ampli não conta no custo. */}
            <AnnulledTitlesList annulled={annulled} />
              </>
            ) : (
              <AppropriationEditor
                open={writebackEnabled && !!editingBillId}
                surfaceMode="embedded"
                writebackEnabled={writebackEnabled}
                readOnly={readOnly}
                onClose={backToList}
                projectId={projectId}
                billId={editingBillId}
                titleInfo={editingTitle}
                onSaved={loadFirstPage}
              />
            )}
          </div>
        </div>
        {open && <div onClick={onClose} className="offcanvas-background" />}
      </div>
    </>
  );
};

export default CostOriginTitlesOffcanvas;
